import React, { ReactNode } from 'react';

import { notification } from 'antd';
import cx from 'classnames';
import { get } from 'lodash';
import { ArgsProps } from 'antd/es/notification/interface';

import { CrossIcon, ExclamationIcon, InfoIcon, TickIcon } from '@/assets/icons';

import styles from './Toast.module.scss';

const DEFAULT_CONFIG = {
	maxCount: 3,
	closeIcon: <CrossIcon className={cx(styles.close)} />,
};

notification.config(DEFAULT_CONFIG);

type ToastTypes = 'success' | 'info' | 'error' | 'warning';

interface IToastConfig extends Omit<ArgsProps, 'message' | 'icon'> {
	maxCount?: number;
}

interface IToastOptions extends IToastConfig {}

const ToastIcons: Record<
	ToastTypes,
	React.JSXElementConstructor<React.SVGAttributes<SVGAElement>>
> = {
	success: TickIcon,
	error: CrossIcon,
	warning: ExclamationIcon,
	info: InfoIcon,
};

function getToastConfig(
	type: ToastTypes,
	message: ReactNode,
	options?: IToastOptions,
) {
	const { description, ...config } = options || { config: {} };
	const ToastIcon = ToastIcons[type];
	notification.config({
		...DEFAULT_CONFIG,
		maxCount: get(config, 'maxCount', DEFAULT_CONFIG.maxCount),
	});
	return {
		message: (
			<div className={styles.toastWrapper}>
				<div className={styles[type]}>
					<ToastIcon className={styles.icon} />
				</div>
				<div className={styles.toastContent}>
					<div className={styles.toastTitle}>{message}</div>
					{description ? (
						<p className={styles.toastDescription}>{description}</p>
					) : null}
				</div>
			</div>
		),
		...config,
		className: styles.toast,
		icon: <></>,
	};
}

export class Toast {
	static success(message: ReactNode, options?: IToastOptions) {
		notification.success(getToastConfig('success', message, options));
	}

	static warning(message: ReactNode, options?: IToastOptions) {
		notification.warning(getToastConfig('warning', message, options));
	}

	static error(message: ReactNode, options?: IToastOptions) {
		notification.error(getToastConfig('error', message, options));
	}

	static info(message: ReactNode, options?: IToastOptions) {
		notification.info(getToastConfig('info', message, options));
	}

	static close(id?: string) {
		notification.destroy(id);
	}
}
